import * as React from "react";
import { Steps, Row, Col, Tooltip, Typography, Space, Modal } from "antd";
import { withTranslation, WithTranslation } from "react-i18next";
import { fileUploadStore, UPLOAD_FAILED } from "./FileUploadStore";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { UploadDragger } from "./UploadDragger";
import { UploadComplete } from "./UploadComplete";
import { FileSubmitter } from "./FileSubmitter";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";
import { documentsTableStore } from "../Stores/DocumentsTableStore";
import { Button } from "Components/General/Button";
import { FileCreateForm } from "../FileCreate/FileCreateForm";
import { templatesStore } from "Pages/Project/Settings/Pages/Templates/Stores/TemplatesStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { plankeyFormStore } from "Pages/Project/Settings/Stores/GeneralFormStore";
import { projectsDataStore } from "Stores";
import { FolderPicker } from "Components/FolderPicker";
import { WithModal, withModal } from "Components/General/Modal/withModal";
import Title from "antd/es/typography/Title";
import { modal } from "Components/General/Modal/AntdModal";

interface FileUploadProps extends WithTranslation, WithModal {}

interface FileUploadState {
  current: number;
}

export const FileUpload = withTranslation(I18N_NS.PROJECT)(
  withModal(
    observer(
      class extends React.Component<FileUploadProps, FileUploadState> {
        constructor(props: FileUploadProps) {
          super(props);

          this.state = {
            current: 0,
          };
        }

        next = () => {
          this.setState({ current: this.state.current + 1 });
        };

        prev = () => {
          fileUploadStore.backToUploadStep();
          this.setState({ current: this.state.current - 1 });
        };

        submit = async () => {
          try {
            if (fileUploadStore.module === "MAIL") {
              await fileUploadStore.moveFiles();
            } else {
              await fileUploadStore.uploadFiles();
            }
            this.next();
          } catch (error) {
            if (error !== UPLOAD_FAILED) {
              throw error;
            }
          }
        };

        create = async (values: API_TemplateUploadRequest) => {
          try {
            await templatesStore.createWithTemplate(values);
            this.next();
          } catch (error) {
            if (error !== UPLOAD_FAILED) {
              throw error;
            }
          }
        };

        reset = () => {
          fileUploadStore.uploadFilesList.forEach((f) => {
            fileUploadStore.deleteFile(f.id);
          });
          fileUploadStore.hideModal();
          this.setState({ current: 0 });
        };

        cancel = () => {
          const resetFunc = this.reset;
          // current === 2 stands for "Summary" step where upload is already finished
          if (this.state.current !== 2 && fileUploadStore.filesMap.size) {
            modal.confirm({
              title: this.props.t("You are going to loose all progress", { ns: I18N_NS.GENERAL }),
              content: this.props.t("Are you sure you want to exit?", { ns: I18N_NS.GENERAL }),
              okText: this.props.t("Yes", { ns: I18N_NS.GENERAL }),
              okType: "primary",
              cancelText: this.props.t("No", { ns: I18N_NS.GENERAL }),
              onOk() {
                resetFunc();
              },
            });
          } else {
            resetFunc();
          }
        };

        handleDownloadFilename = () => {
          plankeyFormStore.downloadFile(projectsDataStore.filenameConvDoc);
        };

        handleFolderSelect = (id: number) => {
          fileUploadStore.setFolderId(id);
          this.next();
        };

        handleSuccess = async () => {
          const { t } = this.props;
          if (fileUploadStore.module === "WORKFLOW" && fileUploadStore.specificWorkflowOptions.onSuccess) {
            fileUploadStore.specificWorkflowOptions.onSuccess();
          } else if (fileUploadStore.module === "MAIL") {
            fileUploadStore.specificCommunicationOptions.onSuccess();
          } else {
            documentsTableStore.refresh();
          }
          this.reset();
          notificationSuccess(t("Success"), t("File(s) uploaded"));
        };

        renderFilenameLink = () => {
          const { t } = this.props;
          return (
            <Space className="upload-file-header-btn">
              <Tooltip title={t("The file that describes the filename rules in the project")}>
                <FontAwesomeIcon icon={["far", plankeyFormStore.isDownloading ? "spinner" : "download"]} />
              </Tooltip>
              <div onClick={this.handleDownloadFilename} className="pointer">
                <Typography.Text underline>{t("Filename convention")}</Typography.Text>
              </div>
            </Space>
          );
        };

        get modalTitle() {
          const { t } = this.props;
          const isFilenameDownloadEnable = projectsDataStore.filenameConvDoc !== 0 && this.state.current === 1;

          if (fileUploadStore.module === "WORKFLOW") {
            return t("Workflow file upload");
          }
          if (fileUploadStore.module === "VIEW") {
            return (
              <Row justify="space-between" align="middle">
                <Col>{t(`View upload`)}</Col>
                {isFilenameDownloadEnable && this.renderFilenameLink()}
              </Row>
            );
          }
          if (fileUploadStore.module === "MAIL") {
            return (
              <Row justify="space-between" align="middle">
                <Col>{t(`Upload files`)}</Col>
                {isFilenameDownloadEnable && this.renderFilenameLink()}
              </Row>
            );
          }
          return (
            <Row justify="space-between" align="middle" style={{ marginRight: 35 }}>
              <Col>
                <Title level={3}>{t(`Upload or create files`)}</Title>
              </Col>
              {this.state.current === 0 && (
                <Button
                  style={{ marginLeft: 20 }}
                  faIcon="plus"
                  type="primary"
                  onClick={() =>
                    this.props.showModal(FileCreateForm, {
                      modalProps: {
                        footer: null,
                        title: t("Create file from template"),
                        width: "100%",
                        style: { top: 20, maxWidth: 680 },
                        maskClosable: true,
                      },
                      componentProps: {
                        onSubmit: this.create,
                      },
                    })
                  }
                >
                  {t("New file from template")}
                </Button>
              )}
              {isFilenameDownloadEnable && this.renderFilenameLink()}
            </Row>
          );
        }

        get activeSteps() {
          const { t } = this.props;

          const firstStep =
            fileUploadStore.module === "MAIL"
              ? {
                  title: "Start",
                  description: t("Select folder"),
                  content: (
                    <FolderPicker
                      onSelectFolder={({ id }) => this.handleFolderSelect(id)}
                      selectedFolderId={fileUploadStore.folderId}
                    />
                  ),
                }
              : {
                  title: "Start",
                  description: t("Choose files"),
                  content: <UploadDragger onNext={this.next} />,
                };

          const steps = [
            firstStep,
            {
              title: "Details",
              description: t("Check and complete"),
              content: <FileSubmitter onBack={this.prev} onSubmit={this.submit} />,
            },
            {
              title: t("Summary"),
              description: t("Upload confirmation"),
              content: <UploadComplete onDone={this.handleSuccess} />,
            },
          ];

          return steps;
        }

        render() {
          const { current } = this.state;

          return (
            <Modal
              style={{ maxWidth: `calc(100vw - 40px)` }}
              width={"100%"}
              className="full-height-modal"
              footer={null}
              title={this.modalTitle}
              open={fileUploadStore.isVisible}
              destroyOnHidden={true}
              onCancel={this.cancel}
            >
              <div className="upload-modal-body" id="upload-modal-body">
                <div className="mb-2">
                  <Steps
                    current={current}
                    items={this.activeSteps.map(({ title, description }) => ({
                      title: title,
                      description: description,
                    }))}
                  />
                </div>
                <div style={{ flexGrow: 1, height: "100%" }}>{this.activeSteps[current].content}</div>
              </div>
            </Modal>
          );
        }
      }
    )
  )
);
